import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const HEADSHOT = "https://customer-assets.emergentagent.com/job_portfolio-refresh-38/artifacts/c8xgqv7u_IMG_5900.jpeg";

const timeline = [
  { era: "2000s", label: "Technology & transformation leadership", desc: "Enterprise technology strategy and organizational change across major institutions." },
  { era: "2017+", label: "Surviving the Future of Work", desc: "Launched the podcast exploring how technology was reshaping careers, skills, and opportunity." },
  { era: "2020+", label: "FutureWise & Education Innovation", desc: "Built systems to help students and families prepare for an AI-driven economy." },
  { era: "2024+", label: "Agentic FuturePath AI", desc: "Founded the company focused on adaptive intelligence, career navigation, and future-readiness." },
  { era: "2026+", label: "Global Thought Leadership", desc: "AI NEXTGEN America recognition. Expanding research, publishing, and advisory globally." },
];

const About = () => {
  return (
    <main className="pt-24 pb-20 min-h-screen">
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-sm uppercase tracking-[0.25em] text-[var(--accent-violet-light)] font-medium">About</p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white italic leading-tight">
              A Futurist Focused on Human Opportunity
            </h1>
            <div className="space-y-4 text-[var(--text-secondary)] leading-relaxed">
              <p>
                Raulvin Coke is a technology executive, futurist, entrepreneur, and Founder & CEO of Agentic FuturePath AI.
              </p>
              <p>
                His work focuses on how artificial intelligence is reshaping human decision-making, education, work, economic opportunity, skills, and institutions.
              </p>
              <p>
                For nearly two decades, he has studied technology transformation and organizational change — evolving through technology leadership, the Surviving the Future of Work podcast, education innovation with FutureWise, and now Agentic FuturePath AI and the framework of Adaptive Intelligence.
              </p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-violet-600/15 to-transparent rounded-3xl blur-xl" />
              <img
                src={HEADSHOT}
                alt="Raulvin Coke portrait"
                className="relative rounded-2xl w-full max-w-sm object-cover shadow-2xl shadow-black/50"
                data-testid="about-portrait"
              />
            </div>
          </motion.div>
        </div>

        {/* Timeline */}
        <section className="mb-20">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white italic mb-10">The Journey</h2>
          <div className="space-y-0">
            {timeline.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * i }}
                className="flex gap-6 group"
              >
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full bg-[var(--accent-violet)] flex-shrink-0 mt-2" />
                  {i < timeline.length - 1 && <div className="w-px flex-1 bg-[rgba(124,58,237,0.2)]" />}
                </div>
                <div className="pb-10">
                  <span className="text-sm font-bold text-[var(--accent-violet-light)]">{item.era}</span>
                  <h3 className="font-display text-xl text-white font-semibold mt-1 mb-2">{item.label}</h3>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Philosophy */}
        <section className="mb-20">
          <div className="bg-[var(--bg-card)] border-glow rounded-2xl p-8 md:p-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white italic mb-6">Personal Philosophy</h2>
            <div className="pl-6 border-l-2 border-[var(--accent-violet)]/40 space-y-4 text-[var(--text-secondary)] leading-relaxed">
              <p>
                The future is not something that happens to people. It is something people can shape — if they have the intelligence, the framework, and the courage to act before the window closes.
              </p>
              <p>
                I believe every person, every family, every institution deserves access to the same quality of foresight that the most powerful organizations in the world use to make decisions.
              </p>
              <p>
                My work is about democratizing that intelligence.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/speaking"
            className="inline-flex items-center gap-2 bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white px-7 py-3.5 rounded-lg font-semibold transition-all"
          >
            Invite Raulvin to Speak
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </main>
  );
};

export default About;
